import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { GeneralInfoTable, IGeneralInfoItem } from '@common/GeneralInfoTable';
import { IProjectDesignModification } from '@designModification/designModification.models';
import { ComponentType, Icon, Tooltip } from '@gcl/react-core';
import { IProjectLoadingRemovalCert } from '@permitToLoadRemove/detail/permitToLoadRemoveDetailPage.models';
import { useNavigate } from '@routes/routes.hooks';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { createTableLinkWithOpenNewTabElement } from '@common/tableColumn.services';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { EDetailPageRightMenuGeneralInfoType } from './detailPageRightMenuGeneralInfo.models';
import './DetailPageRightMenuGeneralInfo.scss';

export interface IDetailPageRightMenuGeneralInfoProps {
  type: EDetailPageRightMenuGeneralInfoType;
  projectName?: string;
  mode?: DetailPageAccessMode;
  masterSchedule?: IMasterScheduleDetail;
  designModification?: IProjectDesignModification;
  loadingRemovalCert?: IProjectLoadingRemovalCert;
}
export const DetailPageRightMenuGeneralInfo: React.FunctionComponent<IDetailPageRightMenuGeneralInfoProps> = ({
  type,
  projectName,
  mode,
  masterSchedule,
  designModification,
  loadingRemovalCert,
}) => {
  const { generateRoutePath } = useNavigate();

  const info = React.useMemo(() => {
    const defaultInfo: IGeneralInfoItem[] = [
      {
        title: 'T1',
        content: createTableLinkWithOpenNewTabElement({
          linkUrl: generateRoutePath(routes.t1.view.path, { id: (masterSchedule?.id ?? '').toString() }),
          linkDisplay: masterSchedule?.refNo as string,
        }),
      },
      { title: 'Package', content: <>{masterSchedule?.designPackage}</> },
    ];

    switch (type) {
      case EDetailPageRightMenuGeneralInfoType.T2:
        defaultInfo.push({
          title: 'Risk Category',
          content: (
            <div className="gcl-gems-detail-page-right-menu-general-info">
              {masterSchedule?.riskCategory?.category}
              {masterSchedule?.riskCategory?.category === 'A' && (
                <Tooltip
                  className="gcl-gems-detail-page-right-menu-general-info-popover"
                  overlay={
                    <>
                      Please record the workshop by uploading the workshop notes
                      <br />
                      to the attachments of this T2 or the comments tab.
                    </>
                  }
                >
                  <Icon iconName="ExclamationCircle" componentType={ComponentType.Danger} className="gcl-ml-xs" />
                </Tooltip>
              )}
            </div>
          ),
        });
        defaultInfo.push({
          title: 'Designer',
          content: <>{masterSchedule?.responsibleDesigner?.name}</>,
        });
        break;
      case EDetailPageRightMenuGeneralInfoType.T3:
        if (designModification)
          defaultInfo.push({
            title: 'Design Change No.',
            content: <>{designModification?.designChangeNo}</>,
          });
        break;
      case EDetailPageRightMenuGeneralInfoType.T4:
        if (loadingRemovalCert)
          defaultInfo.push({
            title: 'Certificate No.',
            content: <>{loadingRemovalCert?.certificateNo}</>,
          });
        break;
      default:
        break;
    }
    return defaultInfo;
  }, [type, JSON.stringify(masterSchedule)]);

  const T1RefNoPackageLabel = (
    <GeneralInfoTable
      titleWidth={4}
      items={info}
      className="gcl-gems-detail-page-right-menu-general-info-table gcl-mb-md"
    />
  );

  return <>{T1RefNoPackageLabel}</>;
};
